import type { Ref } from 'vue';

interface BackPlaces<P extends string> {
  /** The place the history entry behind this page belongs to; undefined when nothing of ours is behind it. */
  backPlace: (previousEntry: string | null | undefined) => P | null | undefined;
  /** The place this page belongs to, where the way back goes when nothing of ours is behind it. */
  homePlace: (path: string) => P;
  routes: Record<P, string>;
}

/**
 * What a back button says and where it goes without history to go back to, shared by the coach
 * shell's and the public layout's. Read once the page has mounted: until the router has written this
 * page's history entry, the entry behind it is still the one behind the page being left.
 */
export const useBackButtonPlace = <P extends string>({ backPlace, homePlace, routes }: BackPlaces<P>) => {
  const route = useRoute();
  const { t } = useI18n();

  const place = ref(null) as Ref<P | null>;
  const fallback = ref<string | undefined>(undefined);

  onMounted(() => {
    const behind = backPlace(window.history.state?.back);
    if (behind !== undefined) {
      place.value = behind;
      return;
    }

    place.value = homePlace(route.path);
    fallback.value = routes[place.value];
  });

  const label = computed(() => (place.value
    ? t('unified.activity.back.toPlace', { place: t(`unified.nav.${place.value}`) })
    : t('unified.activity.back.plain')));

  return { fallback, label };
};

/**
 * A way out held from its press until the router lands, as the coach shell's leaving is: a second
 * press in between is the same leaving asked for twice, and honoured twice it would step back a page
 * further than the student asked to go.
 */
export const useOneLeaving = () => {
  const router = useRouter();
  let leaving = false;

  return (leave: () => void) => {
    if (leaving) {
      return;
    }
    leaving = true;
    const stop = router.afterEach(() => {
      stop();
      leaving = false;
    });
    leave();
  };
};
